import React, { useState, useEffect } from 'react';
import { X, Brain, HelpCircle, Loader, ArrowRight } from 'lucide-react';
import { apiConfig } from '../../config/api.config';

interface ContextQuestion {
  id: string;
  type: 'text' | 'select' | 'multiselect' | 'slider' | 'radio';
  question: string;
  description?: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  min?: number;
  max?: number;
  default?: any;
  labels?: string[];
}

interface ContextQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  goalId: string;
  goalName: string;
  onSubmit: (answers: Record<string, any>) => void;
  loading?: boolean;
}

const ContextQuestionModal: React.FC<ContextQuestionModalProps> = ({
  isOpen,
  onClose,
  goalId,
  goalName,
  onSubmit,
  loading = false
}) => {
  const [questions, setQuestions] = useState<ContextQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [projectContext, setProjectContext] = useState<any>(null);

  useEffect(() => {
    if (isOpen && goalId) {
      fetchContextQuestions();
    }
  }, [isOpen, goalId]);

  const fetchContextQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const response = await fetch(apiConfig.endpoints.ai.generateContextQuestions, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('projectai_token')}`
        },
        body: JSON.stringify({
          goalId,
          goalName
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch context questions');
      }

      const data = await response.json();
      setQuestions(data.questions || []);
      setProjectContext(data.projectContext);

      // Initialize answers with default values
      const initialAnswers: Record<string, any> = {};
      data.questions?.forEach((q: ContextQuestion) => {
        if (q.default !== undefined) {
          initialAnswers[q.id] = q.default;
        }
      });
      setAnswers(initialAnswers);
    } catch (error) {
      console.error('Error fetching context questions:', error);
      // Fallback to default questions if API fails
      const fallbackQuestions = generateFallbackQuestions(goalName);
      setQuestions(fallbackQuestions);

      // Initialize default answers
      const initialAnswers: Record<string, any> = {};
      fallbackQuestions.forEach((q: ContextQuestion) => {
        if (q.default !== undefined) {
          initialAnswers[q.id] = q.default;
        }
      });
      setAnswers(initialAnswers);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNextStep = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    onSubmit(answers);
  };

  const renderQuestion = (question: ContextQuestion) => {
    switch (question.type) {
      case 'text':
        return (
          <div>
            <input
              type="text"
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        );

      case 'select':
        return (
          <div>
            <select
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Bitte wählen...</option>
              {question.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );

      case 'multiselect':
        return (
          <div className="space-y-2">
            {question.options?.map(option => (
              <label key={option.value} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={(answers[question.id] || []).includes(option.value)}
                  onChange={(e) => {
                    const currentValues = answers[question.id] || [];
                    const newValues = e.target.checked
                      ? [...currentValues, option.value]
                      : currentValues.filter((v: string) => v !== option.value);
                    handleAnswerChange(question.id, newValues);
                  }}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {question.options?.map(option => (
              <label key={option.value} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={question.id}
                  value={option.value}
                  checked={answers[question.id] === option.value}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  className="border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'slider':
        const value = answers[question.id] || question.min || 1;
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">{question.labels?.[0] || question.min}</span>
              <input
                type="range"
                min={question.min || 1}
                max={question.max || 5}
                value={value}
                onChange={(e) => handleAnswerChange(question.id, parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <span className="text-sm text-gray-500">{question.labels?.[question.labels.length - 1] || question.max}</span>
            </div>
            <div className="text-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {value} {question.labels && question.labels[value - 1] ? `(${question.labels[value - 1]})` : ''}
              </span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Fallback questions generator
  const generateFallbackQuestions = (goalName: string): ContextQuestion[] => {
    const baseQuestions: ContextQuestion[] = [
      {
        id: 'priority',
        type: 'slider',
        question: 'Wie hoch ist die Priorität dieses Ziels?',
        description: 'Hilft bei der Zeitplanung und Ressourcenverteilung',
        min: 1,
        max: 5,
        default: 3,
        labels: ['Sehr niedrig', 'Niedrig', 'Mittel', 'Hoch', 'Sehr hoch']
      },
      {
        id: 'timeline',
        type: 'select',
        question: 'In welchem Zeitrahmen soll dieses Ziel erreicht werden?',
        options: [
          { value: 'sprint', label: '1-2 Wochen (Sprint)' },
          { value: 'month', label: '1 Monat' },
          { value: 'quarter', label: '3 Monate (Quartal)' },
          { value: 'halfyear', label: '6 Monate' },
          { value: 'year', label: '1 Jahr oder mehr' }
        ],
        default: 'quarter'
      },
      {
        id: 'complexity_preference',
        type: 'radio',
        question: 'Welchen Entwicklungsansatz bevorzugen Sie?',
        options: [
          { value: 'mvp', label: 'MVP-First (Schnell testbare Grundversion)' },
          { value: 'comprehensive', label: 'Vollständige Lösung von Anfang an' },
          { value: 'iterative', label: 'Schrittweise Entwicklung mit Feedback-Schleifen' }
        ],
        default: 'mvp'
      }
    ];

    // Goal-specific questions
    const goalLowerCase = goalName.toLowerCase();

    if (goalLowerCase.includes('content') || goalLowerCase.includes('tool') || goalLowerCase.includes('creation')) {
      baseQuestions.push({
        id: 'content_types',
        type: 'multiselect',
        question: 'Welche Content-Typen sollen unterstützt werden?',
        options: [
          { value: 'text', label: 'Text/Artikel' },
          { value: 'quiz', label: 'Quizze/Tests' },
          { value: 'video', label: 'Videos' },
          { value: 'interactive', label: 'Interaktive Inhalte' },
          { value: 'documents', label: 'Dokumente (PDF, etc.)' }
        ]
      });

      baseQuestions.push({
        id: 'target_audience',
        type: 'text',
        question: 'Wer ist die primäre Zielgruppe?',
        placeholder: 'z.B. Studenten, Fachkräfte, Trainer, Content-Ersteller...',
        description: 'Hilft bei der Gestaltung der Features und Benutzeroberfläche'
      });
    }

    if (goalLowerCase.includes('ai') || goalLowerCase.includes('ki')) {
      baseQuestions.push({
        id: 'ai_features',
        type: 'multiselect',
        question: 'Welche KI-Features sind am wichtigsten?',
        options: [
          { value: 'generation', label: 'Automatische Content-Generierung' },
          { value: 'validation', label: 'Qualitäts-Validierung' },
          { value: 'personalization', label: 'Personalisierung' },
          { value: 'translation', label: 'Übersetzungen' },
          { value: 'analytics', label: 'Intelligente Analysen' }
        ]
      });
    }

    return baseQuestions;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Brain className="h-6 w-6 text-purple-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                KI-Kontext für "{goalName}"
              </h3>
              <p className="text-sm text-gray-500">
                Beantworten Sie einige Fragen für eine präzisere AI-Dekomposition
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loadingQuestions ? (
            <div className="flex items-center justify-center p-12">
              <div className="text-center">
                <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-500">Generiere kontextuelle Fragen...</p>
              </div>
            </div>
          ) : questions.length > 0 ? (
            <div className="p-6">
              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                  <span>Frage {currentStep + 1} von {questions.length}</span>
                  <span>{Math.round(((currentStep + 1) / questions.length) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Current Question */}
              {questions[currentStep] && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      {questions[currentStep].question}
                    </h4>
                    {questions[currentStep].description && (
                      <div className="flex items-start space-x-2 mb-4 p-3 bg-blue-50 rounded-lg">
                        <HelpCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-blue-700">
                          {questions[currentStep].description}
                        </p>
                      </div>
                    )}
                  </div>

                  {renderQuestion(questions[currentStep])}
                </div>
              )}

              {/* Project Context Info */}
              {projectContext && currentStep === 0 && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-2">Projekt-Kontext</h5>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Projekt:</span>
                      <span className="ml-2 font-medium">{projectContext.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Typ:</span>
                      <span className="ml-2 font-medium">{projectContext.type}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center p-12">
              <p className="text-gray-500">Keine Fragen verfügbar</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loadingQuestions && questions.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 min-h-[80px]">
            <button
              onClick={handlePrevStep}
              disabled={currentStep === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Zurück
            </button>

            <div className="flex items-center space-x-3">
              {currentStep < questions.length - 1 ? (
                <button
                  onClick={handleNextStep}
                  className="flex items-center space-x-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <span>Weiter</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center space-x-2 px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Brain className="h-4 w-4" />
                  )}
                  <span>{loading ? 'KI analysiert...' : 'KI-Dekomposition starten'}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextQuestionModal;
